import { useCallback, useMemo, useState } from 'react';
import {
  Autocomplete,
  Checkbox,
  Chip,
  TextField,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import { type FilterOptionsState } from '@mui/material/useAutocomplete';
import AddIcon from '@mui/icons-material/Add';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const ADD_NEW_OPTION = '__add_new_option__';
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const uncheckedIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;

interface CategoryMultiSelectProps {
  label: string;
  codes: string[];
  getLabel: (code: string) => string;
  value: string[];
  onChange: (codes: string[]) => void;
  onAddNew: (searchText: string) => void;
  addNewLabel: string;
  required?: boolean;
  error?: string;
  loading?: boolean;
}

export const CategoryMultiSelect = ({
  label,
  codes,
  getLabel,
  value,
  onChange,
  onAddNew,
  addNewLabel,
  required,
  error,
  loading,
}: CategoryMultiSelectProps): JSX.Element => {
  const [inputValue, setInputValue] = useState('');

  const options = useMemo(() => [...codes, ADD_NEW_OPTION], [codes]);

  const filterOptions = useCallback(
    (_opts: string[], state: FilterOptionsState<string>): string[] => {
      const q = state.inputValue.trim().toLowerCase();
      const matches = codes.filter(
        (code) =>
          code.toLowerCase().includes(q) ||
          getLabel(code).toLowerCase().includes(q)
      );
      return [...matches, ADD_NEW_OPTION];
    },
    [codes, getLabel]
  );

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      options={options}
      filterOptions={filterOptions}
      inputValue={inputValue}
      onInputChange={(_e, newInputValue) => {
        setInputValue(newInputValue);
      }}
      value={value}
      loading={loading}
      getOptionLabel={(option) =>
        option === ADD_NEW_OPTION ? addNewLabel : getLabel(option)
      }
      isOptionEqualToValue={(option, val) => option === val}
      onChange={(_e, selected) => {
        if (selected.includes(ADD_NEW_OPTION)) {
          onAddNew(inputValue.trim());
          return;
        }
        onChange(selected);
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option}
            size="small"
            label={getLabel(option)}
          />
        ))
      }
      renderOption={(liProps, option, state) => {
        if (option === ADD_NEW_OPTION) {
          return (
            <Box
              component="li"
              {...liProps}
              key={ADD_NEW_OPTION}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <AddIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="body2" fontWeight={600} color="primary.main">
                {addNewLabel}
              </Typography>
            </Box>
          );
        }
        return (
          <li {...liProps} key={option}>
            <Checkbox
              icon={uncheckedIcon}
              checkedIcon={checkedIcon}
              checked={state.selected}
              size="small"
              sx={{ mr: 1 }}
            />
            {getLabel(option)}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={required ? `${label} *` : label}
          size="small"
          error={!!error}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
